import { FormInput } from "@/components/forms/FormInput";
import { FormSubmitButton } from "@/components/forms/FormSubmitButton";
import { FormWrapper } from "@/components/forms/FormWrapper";
import { backoffice, OrganizationItem } from "@/endpoints/dryp/backoffice";
import { useAsyncStateStore } from "@/hooks/useAsyncStateStore";
import { useFormHandler } from "@/hooks/useFormHandler";
import { useStore } from "@/hooks/useStore";
import { DialogHeader } from "@/components/ui/dialog";
import { DialogDescription, DialogTitle } from "@radix-ui/react-dialog";
import { observer } from "mobx-react-lite";
import { FC } from "react";
import { z } from "zod";

const validationSchema = z.object({
	name: z.string().min(1, "Name is required"),
	description: z.string().optional(),
});

type FormValues = z.infer<typeof validationSchema>;

interface AddEditOrganizationProps {
	organization?: OrganizationItem;
	onSuccess: () => void;
}

export const AddEditOrganization: FC<AddEditOrganizationProps> = observer(({ organization, onSuccess }) => {
	const {
		uiStore: { modalStore },
	} = useStore();
	const isEditing = !!organization;
	const createApi = useAsyncStateStore(backoffice.createOrganization);
	const updateApi = useAsyncStateStore(backoffice.updateOrganization);
	const { form, handleFormErrors } = useFormHandler(validationSchema, {
		defaultValues: {
			name: organization?.name ?? "",
			description: organization?.description ?? "",
		},
	});

	const onSubmit = async (data: FormValues) => {
		try {
			if (isEditing) {
				await updateApi.fetch(organization.id, data);
			} else {
				await createApi.fetch(data);
			}
			modalStore.close();
			onSuccess();
		} catch (error) {
			handleFormErrors(error);
		}
	};

	const loading = createApi.loading || updateApi.loading;
	const error = createApi.error || updateApi.error;
	const title = isEditing ? "Edit Organization" : "Add Organization";

	return (
		<>
			<DialogHeader>
				<DialogTitle>{title}</DialogTitle>
				<DialogDescription className="sr-only">
					Fill out the form to {isEditing ? "edit" : "add"} organization.
				</DialogDescription>
			</DialogHeader>

			<FormWrapper form={form} onSubmit={onSubmit} error={error}>
				<FormInput name="name" type="text" label="Name" placeholder="Organization name" required />
				<FormInput name="description" type="text" label="Description" placeholder="Description" />
				<FormSubmitButton
					isLoading={loading}
					buttonText={isEditing ? "Save changes" : "Create organization"}
					buttonLoadingText={isEditing ? "Saving changes" : "Creating organization"}
				/>
			</FormWrapper>
		</>
	);
});
